/**
 * Guess an organization name from a work email, for the signup form's
 * Organization/District field.
 *
 * Only a suggestion. It is pre-filled visibly greyed and editable, and the user is
 * expected to correct it — "boystown.org" cannot be turned back into "Boys Town" by
 * any rule, and nothing here tries.
 */

/**
 * Consumer mail providers, which never get a suggestion.
 *
 * Without this the first person signing up from Gmail would be offered "Gmail" as their
 * organization name, and everyone who accepted it would be pooled into one org with
 * strangers — `_find_or_create_trial_org` joins by name. Erring toward no suggestion is
 * always safe; the field is required, so the user just types what they would have typed
 * anyway.
 *
 * Mirrors `PUBLIC_EMAIL_DOMAINS` in `backend/app/join_request/constants.py`, which
 * blocks the same providers from being *claimed* as an org's domain. The two lists serve
 * different rules and are allowed to be edited independently, but a provider added there
 * should be added here too.
 */
export const PUBLIC_EMAIL_PROVIDERS: ReadonlySet<string> = new Set([
  'aol.com',
  'gmail.com',
  'googlemail.com',
  'hotmail.co.uk',
  'hotmail.com',
  'icloud.com',
  'live.com',
  'mail.com',
  'me.com',
  'msn.com',
  'outlook.com',
  'pm.me',
  'proton.me',
  'protonmail.com',
  'yahoo.co.uk',
  'yahoo.com',
  'ymail.com',
  'zoho.com',
]);

/**
 * Sub-domain labels that name a mail host rather than the organization, so
 * "mail.elastiq.com" suggests "Elastiq" and not "Mail".
 */
const MAIL_HOST_LABELS: ReadonlySet<string> = new Set([
  'email',
  'mail',
  'mx',
  'smtp',
  'webmail',
  'www',
]);

/** Title-case one domain label: "abc-unified" → "Abc Unified". */
function titleCase(label: string): string {
  return label
    .split(/[-_]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The organization name to offer for this email, or null to offer nothing.
 *
 * Returns null for anything it isn't confident about — a consumer provider, a malformed
 * address, or a domain whose meaningful label is too short to be a name. A missing
 * suggestion leaves the field exactly as it is today; a wrong one is something the user
 * has to notice and undo.
 */
export function suggestOrgNameFromEmail(email: string): string | null {
  const at = (email || '').trim().toLowerCase().lastIndexOf('@');
  if (at <= 0) return null;

  const domain = email.trim().toLowerCase().slice(at + 1);
  if (!domain.includes('.') || /\s/.test(domain)) return null;
  if (PUBLIC_EMAIL_PROVIDERS.has(domain)) return null;

  // Drop mail-host prefixes, then take the first remaining label. Deliberately not a
  // public-suffix lookup: "springfield.k12.ca.us" and "elastiq.ai" both want their first
  // label, and carrying a suffix list for a pre-fill hint is not worth the weight.
  const labels = domain.split('.').filter(Boolean);
  while (labels.length > 1 && MAIL_HOST_LABELS.has(labels[0])) {
    labels.shift();
  }

  const main = labels[0];
  // One or two characters is an initialism at best and a typo at worst; "co", "hq" and
  // the like make a worse default than an empty field.
  if (!main || main.length < 3) return null;

  const suggestion = titleCase(main);
  return suggestion || null;
}
